import type { MouseEvent, ReactNode } from "react";
import { createElement, Fragment } from "react";

export const CHAT_MENTION_ALL_LABEL = "@所有人";
const CHAT_MENTION_ALL_NAME = "所有人";

export interface ChatMentionProduct {
  spaceProductId: string;
  displayName: string;
  productKind?: string;
}

export function toChatMentionProductsFromStartup(
  list: Array<{ spaceProductId?: string; displayName?: string; productKind?: string }>
): ChatMentionProduct[] {
  const out: ChatMentionProduct[] = [];
  for (const row of list) {
    const spaceProductId = row.spaceProductId?.trim() ?? "";
    const displayName = row.displayName?.trim() ?? "";
    if (!spaceProductId || !displayName) continue;
    out.push({
      spaceProductId,
      displayName,
      productKind: row.productKind?.trim() || undefined,
    });
  }
  return out;
}

function productsForMatch(catalog: ChatMentionProduct[]): ChatMentionProduct[] {
  const seen = new Set<string>();
  const out: ChatMentionProduct[] = [];
  for (const p of catalog) {
    const name = p.displayName?.trim();
    if (!name || seen.has(name)) continue;
    seen.add(name);
    out.push({ ...p, displayName: name });
  }
  return out.sort((a, b) => b.displayName.length - a.displayName.length);
}

type MentionMatch =
  | { kind: "all"; label: string; length: number }
  | { kind: "product"; product: ChatMentionProduct; label: string; length: number };

function matchMentionAt(text: string, index: number, products: ChatMentionProduct[]): MentionMatch | null {
  if (text[index] !== "@") return null;
  const start = index + 1;
  if (text.startsWith(CHAT_MENTION_ALL_NAME, start)) {
    return { kind: "all", label: CHAT_MENTION_ALL_LABEL, length: 1 + CHAT_MENTION_ALL_NAME.length };
  }
  for (const p of products) {
    const nm = p.displayName.trim();
    if (nm && text.startsWith(nm, start)) {
      return { kind: "product", product: p, label: `@${nm}`, length: 1 + nm.length };
    }
  }
  return null;
}

/** 将 @所有人 / @产品名 渲染为蓝色；产品可点击跳转 */
export function renderChatMentionText(
  text: string | null | undefined,
  catalog: ChatMentionProduct[],
  options?: {
    onProductClick?: (product: ChatMentionProduct) => void;
    renderPlain?: (plain: string) => ReactNode;
  }
): ReactNode {
  const src = String(text ?? "");
  if (!src) return src;
  const products = productsForMatch(catalog);
  const renderPlain = options?.renderPlain ?? ((plain: string) => plain);

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (let i = 0; i < src.length; ) {
    const matched = matchMentionAt(src, i, products);
    if (matched) {
      if (i > lastIndex) {
        parts.push(createElement(Fragment, { key: `plain-${key++}` }, renderPlain(src.slice(lastIndex, i))));
      }
      if (matched.kind === "all") {
        parts.push(
          createElement(
            "span",
            { key: `mention-${key++}`, className: "crm-chat-mention crm-chat-mention--all" },
            matched.label
          )
        );
      } else {
        const product = matched.product;
        const clickable = Boolean(options?.onProductClick);
        parts.push(
          createElement(
            clickable ? "button" : "span",
            {
              key: `mention-${key++}`,
              type: clickable ? "button" : undefined,
              className: "crm-chat-mention crm-chat-mention--product",
              onClick: clickable
                ? (e: MouseEvent) => {
                    e.stopPropagation();
                    options?.onProductClick?.(product);
                  }
                : undefined,
            },
            matched.label
          )
        );
      }
      i += matched.length;
      lastIndex = i;
    } else {
      i += 1;
    }
  }

  if (lastIndex < src.length) {
    parts.push(createElement(Fragment, { key: `plain-${key++}` }, renderPlain(src.slice(lastIndex))));
  }

  return parts.length > 0 ? createElement(Fragment, null, ...parts) : src;
}
